"use client";

import { useState } from "react";

import { getTprDepartmentName, getTprDepartmentIds } from "@/configuration/groups/tpr/athena";
import { refreshTprWorkflowProgress } from "@/configuration/groups/tpr/components/workflow-progress";
import { listTprPhysiciansAtDepartment } from "@/configuration/groups/tpr/workflows/physician-roster";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Button } from "@/src/components/shadcn/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/shadcn/ui/select";
import { toast } from "@/src/hooks/use-toast";
import {
  useLocalTaskUpdateMutation,
  useOverrideTprReceivingAssignmentMutation,
} from "@/src/lib/data-fetchers/tasks-fetchers";
import { type Task, TaskStatus } from "@/src/models/task";

const DEPARTMENT_OPTIONS = getTprDepartmentIds()
  .map((departmentId) => ({ departmentId, name: getTprDepartmentName(departmentId) ?? departmentId }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * Lets ops set the receiving provider and location themselves when automation picked the wrong pair
 * (or none at all), then replays the provider-scoped decisions downstream.
 *
 * The provider list is scoped to the selected location, and the roster is the only source: offering
 * a provider who doesn't practise where the referral is going is what produces a care-team member
 * at the wrong office, so those pairs aren't offered at all. Changing the location clears a provider
 * who doesn't practise at the new one rather than silently keeping an invalid pair.
 */
export function TprReceivingAssignmentEditor({
  task,
  currentProviderNpi,
  currentDepartmentId,
}: {
  task: Task;
  currentProviderNpi: string | null;
  currentDepartmentId: string | null;
}) {
  const [departmentId, setDepartmentId] = useState(currentDepartmentId ?? "");
  const [providerNpi, setProviderNpi] = useState(currentProviderNpi ?? "");
  const overrideMutation = useOverrideTprReceivingAssignmentMutation();
  const localTaskUpdateMutation = useLocalTaskUpdateMutation({ invalidateOnSettled: false });
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");

  const providerOptions = departmentId ? listTprPhysiciansAtDepartment(departmentId) : [];
  const isUnchanged = departmentId === currentDepartmentId && providerNpi === currentProviderNpi;
  // Deliberately not gated on anything else about the task. A pending approval elsewhere used to
  // make this refuse server-side; the replay now widens to cover that gate instead, so ops setting
  // the provider and location by hand always applies.
  const canSave = Boolean(departmentId && providerNpi) && !isUnchanged && !overrideMutation.isPending;

  const handleDepartmentChange = (nextDepartmentId: string) => {
    setDepartmentId(nextDepartmentId);

    // Computed from the next location, not the stale `providerOptions` above.
    const stillPractisesHere = listTprPhysiciansAtDepartment(nextDepartmentId).some(
      (option) => option.providerNpi === providerNpi
    );

    if (!stillPractisesHere) setProviderNpi("");
  };

  const handleSave = async () => {
    try {
      const { replayedStepKeys } = await overrideMutation.mutateAsync({
        taskId: task.id,
        providerNpi,
        targetDepartmentId: departmentId,
      });

      localTaskUpdateMutation.mutate({ ...task, status: TaskStatus.PROCESSING });
      refreshTprWorkflowProgress(invalidateInstructions);
      toast({
        title: "Receiving assignment updated",
        description: replayedStepKeys.length
          ? `Re-running ${replayedStepKeys.length} downstream steps against the new provider and location.`
          : "The workflow will use the new provider and location from here on.",
      });
    } catch (error) {
      toast({
        title: "Could not update the receiving assignment",
        description: error instanceof Error ? error.message : "Refresh the task and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">Change the provider or location</p>
      <div className="flex flex-wrap gap-3">
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-muted-foreground">
          Location
          <Select value={departmentId} onValueChange={handleDepartmentChange}>
            <SelectTrigger className="bg-background" aria-label="Receiving location">
              <SelectValue placeholder="Select a location" />
            </SelectTrigger>
            <SelectContent>
              {DEPARTMENT_OPTIONS.map((option) => (
                <SelectItem key={option.departmentId} value={option.departmentId}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-xs text-muted-foreground">
          Provider
          <Select value={providerNpi} onValueChange={setProviderNpi} disabled={!departmentId}>
            <SelectTrigger className="bg-background" aria-label="Receiving provider">
              <SelectValue placeholder={departmentId ? "Select a provider" : "Select a location first"} />
            </SelectTrigger>
            <SelectContent>
              {providerOptions.map((option) => (
                <SelectItem key={option.providerNpi} value={option.providerNpi}>
                  {option.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>
      {departmentId && providerOptions.length === 0 ? (
        <p className="text-sm text-warn">
          No rostered provider practises at {getTprDepartmentName(departmentId) ?? departmentId}. Pick another location,
          or ask ops to add the provider to the roster.
        </p>
      ) : null}
      {/* Spelled out because a location change is far-reaching: whether the destination is
          Camelback decides which steps run at all, so ops should expect the whole downstream set to
          move, not just the provider's name. */}
      <p className="text-xs text-muted-foreground">
        Saving re-runs everything that depends on the assignment: the chart&apos;s department, care team, document
        routing, queue priority, insurance network, authorization, the scheduling recommendation, and the disposition
        note. Moving a referral on or off Camelback changes which of those apply. Already-uploaded documents and the
        confirmed insurance coverage are left alone.
      </p>
      <div className="flex justify-end">
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {overrideMutation.isPending ? "Saving..." : "Save and re-decide"}
        </Button>
      </div>
    </div>
  );
}
